import { t, useLanguage } from "@/lib/i18n";
import { spouseIds } from "@/lib/spouses";
import React, { useEffect, useState, useMemo, useRef, useImperativeHandle, forwardRef } from "react";
import { TransformWrapper, TransformComponent, useControls } from "react-zoom-pan-pinch";
import { fitTree, constrainTreePan } from "@/lib/treeViewport";
import { fileUrl } from "@/lib/api";
import { computeDepths, buildUnits, collapseAncestors } from "@/lib/tree";
import { displayMemberName } from "@/lib/memberName";
import { memberPhoto, cardPhotoStyle, cardPhotoImage } from "@/lib/memberPhoto";
import { ZoomIn, ZoomOut, Maximize, User, Minus, Plus, Scan, FoldVertical, UnfoldVertical } from "lucide-react";

const NODE_W = 140;
const NODE_H = 176;
const H_GAP = 32;
const V_GAP = 64;

const COUPLE_GAP = 20;
const SLOT = NODE_W + H_GAP;

function readableTreeView(viewport, start) {
  const scale = Math.max(0.35, Math.min(1.1, (viewport.width - 48) / (2 * NODE_W + COUPLE_GAP),
    (viewport.height - 152) / (3 * NODE_H + 2 * V_GAP)));
  return { scale, x: viewport.width / 2 - start.x * scale, y: 72 - start.y * scale };
}

// Subtree-allocation layout: couples are units, parents centered above child units.
function computeLayout(persons, collapsedKeys) {
  const collapsed = collapsedKeys || new Set();
  const { byId, unitOf, units, childrenOf } = buildUnits(persons);
  units.forEach((u) => { u.slots = 0; u.kids = []; });

  const roots = units.filter(
    (u) => !u.members.some((m) => (m.father_id && byId[m.father_id]) || (m.mother_id && byId[m.mother_id]))
  );

  // hidden = all transitive descendant units of any collapsed unit
  const hidden = new Set();
  units.forEach((u) => {
    if (collapsed.has(u.key)) {
      const stack = [...(childrenOf.get(u) || [])];
      while (stack.length) {
        const c = stack.pop();
        if (hidden.has(c)) continue;
        hidden.add(c);
        (childrenOf.get(c) || []).forEach((g) => stack.push(g));
      }
    }
  });

  const visited = new Set(hidden);
  function measure(u) {
    visited.add(u);
    const isCollapsed = collapsed.has(u.key);
    const kids = isCollapsed ? [] : [...(childrenOf.get(u) || [])].filter((k) => !visited.has(k) && !hidden.has(k));
    u.kids = kids;
    let w = u.members.length;
    if (kids.length) {
      let sum = 0;
      kids.forEach((k) => (sum += measure(k)));
      w = Math.max(w, sum);
    }
    u.slots = w;
    return w;
  }

  const positions = {};
  function place(u, startSlot) {
    const spanPx = u.slots * SLOT;
    const unitPx = u.members.length * NODE_W + (u.members.length - 1) * COUPLE_GAP;
    const ux = startSlot * SLOT + Math.max(0, (spanPx - unitPx) / 2);
    u.members.forEach((m, i) => {
      positions[m.id] = { x: ux + i * (NODE_W + COUPLE_GAP), y: u.depth * (NODE_H + V_GAP) };
    });
    const kidsSlots = u.kids.reduce((a, k) => a + k.slots, 0);
    let childStart = startSlot + Math.max(0, (u.slots - kidsSlots) / 2);
    u.kids.forEach((k) => {
      place(k, childStart);
      childStart += k.slots;
    });
  }

  // measure & place roots; any unvisited units (cycles) become extra roots
  const allRoots = [...roots, ...units.filter((u) => !visited.has(u) && !roots.includes(u))];
  allRoots.forEach((u) => { if (!visited.has(u)) measure(u); });
  let cursor = 0;
  let maxDepth = 0;
  allRoots.forEach((u) => {
    if (!positions[u.members[0].id]) place(u, cursor);
    cursor += u.slots;
  });
  units.forEach((u) => { if (positions[u.members[0].id]) maxDepth = Math.max(maxDepth, u.depth); });

  // per-person metadata for collapse handles
  const nodeMeta = {};
  units.forEach((u) => {
    const childCount = (childrenOf.get(u) || new Set()).size;
    u.members.forEach((m, i) => {
      nodeMeta[m.id] = { familyKey: u.key, hasDescendants: childCount > 0, collapsed: collapsed.has(u.key), primary: i === 0 };
    });
  });

  const width = Math.max(cursor * SLOT, NODE_W) + H_GAP;
  const height = (maxDepth + 1) * (NODE_H + V_GAP);
  return { positions, width, height, nodeMeta };
}

const Controls = ({ viewport, bounds, start, positions, fitRevision, onCollapseAll, onExpandAll, focusRequest, onFocusHandled }) => {
  useLanguage();
  const { zoomIn, zoomOut, setTransform, zoomToElement } = useControls();
  const fittedView = useRef(false);
  const fitView = (maxScale = 1.1, duration = 300) => {
    fittedView.current = true;
    const fit = fitTree(viewport, bounds, maxScale);
    setTransform(fit.x, fit.y, fit.scale, duration);
  };
  const readableView = () => {
    fittedView.current = false;
    // Keep compact cards near natural size so larger screens show more members.
    const view = readableTreeView(viewport, start);
    setTransform(view.x, view.y, view.scale, 250);
  };
  const resetRef = useRef(readableView);
  resetRef.current = () => fittedView.current ? fitView() : readableView();
  useEffect(() => {
    // Gotra changes remount the canvas. Wait until the transform wrapper has
    // initialized, then run the same action as "Show larger member cards".
    const frame = requestAnimationFrame(() => resetRef.current());
    return () => cancelAnimationFrame(frame);
  }, [viewport.width, viewport.height]);
  useEffect(() => {
    if (!focusRequest) return;
    // Wait for the new gotra canvas and expanded nodes to mount before focusing.
    const frame = requestAnimationFrame(() => {
      const node = document.getElementById(`ptnode-${focusRequest.id}`);
      if (node) {
        fittedView.current = false;
        const position = positions[focusRequest.id];
        if (focusRequest.align === "top" && position) {
          const scale = Math.min(1.1, Math.max(0.35, (viewport.width - 48) / (2 * NODE_W + COUPLE_GAP)));
          setTransform(viewport.width / 2 - (position.x + 20 + NODE_W / 2) * scale,
            72 - (position.y + 20) * scale, scale, 500);
        } else if (focusRequest.align === "center" && position) {
          // Use final layout coordinates: the DOM card may still be transitioning.
          const scale = Math.max(0.01, Math.min(1.1,
            (viewport.width - 48) / NODE_W, (viewport.height - 152) / (NODE_H + 32)));
          setTransform(viewport.width / 2 - (position.x + 20 + NODE_W / 2) * scale,
            viewport.height / 2 - (position.y + 20 + NODE_H / 2) * scale, scale, 300);
        } else zoomToElement(node, 1.1, 500);
        onFocusHandled(focusRequest);
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [focusRequest, onFocusHandled, zoomToElement, positions, viewport.width, viewport.height, setTransform]);
  const lastFitRevision = useRef(fitRevision);
  useEffect(() => {
    if (lastFitRevision.current === fitRevision) return;
    const frame = requestAnimationFrame(() => {
      fittedView.current = true;
      const fit = fitTree(viewport, bounds, 1.1);
      setTransform(fit.x, fit.y, fit.scale, 300);
      lastFitRevision.current = fitRevision;
    });
    return () => cancelAnimationFrame(frame);
  }, [fitRevision, viewport, bounds, setTransform]);
  const btn = "w-11 h-11 shrink-0 rounded-lg bg-[#2C1E16] text-amber-50 flex items-center justify-center hover:bg-[#D4A359] hover:text-[#2C1E16] transition-colors shadow-lg";
  return (
    <>
    <div className="tree-zoom-controls absolute z-20 flex gap-1 sm:gap-2" data-testid="tree-zoom-controls">
      <button className={btn} onClick={() => zoomIn()} data-testid="zoom-in-button" aria-label={t("Zoom in")}><ZoomIn size={18} /></button>
      <button className={btn} onClick={() => zoomOut()} data-testid="zoom-out-button" aria-label={t("Zoom out")}><ZoomOut size={18} /></button>
      <button className={btn} onClick={onCollapseAll} data-testid="collapse-all-button" aria-label={t("Collapse all")} title={t("Collapse all")}><FoldVertical size={18} /></button>
      <button className={btn} onClick={onExpandAll} data-testid="expand-all-button" aria-label={t("Expand all")} title={t("Expand all")}><UnfoldVertical size={18} /></button>
      <button className={btn} onClick={() => fitView()} data-testid="zoom-reset-button" aria-label={t("Fit entire tree to screen")} title={t("Fit entire tree")}><Maximize size={18} /></button>
      <button className={btn} onClick={readableView} data-testid="readable-view-button" aria-label={t("Show larger member cards")} title={t("Show larger member cards")}><Scan size={18} /></button>
    </div>
    </>
  );
};

function Node({ person, pos, selected, onSelect, meta, onToggleCollapse, ancestorMeta, onToggleAncestors }) {
  useLanguage();
  const photo = cardPhotoImage(memberPhoto(person));
  const name = displayMemberName({ ...person, is_alive: true });
  const cardName = [name, person.gotra?.trim()].filter(Boolean).join(" ");
  const deceased = person.is_alive === false;
  const cardTone = person.gender === "Male"
    ? "bg-blue-50 border-blue-700" : person.gender === "Female"
      ? "bg-pink-50 border-pink-700" : "bg-white border-stone-600";
  const avatarTone = deceased ? "border-stone-600 bg-stone-200 text-stone-600" : person.gender === "Female"
    ? "border-green-700 bg-pink-100 text-pink-700" : "border-green-700 bg-blue-100 text-blue-700";
  return (
    <div
      id={`ptnode-${person.id}`}
      className={`tree-member-card absolute rounded-2xl border-2 ${cardTone} cursor-pointer ${
        selected ? "tree-member-card-selected" : ""
      }`}
      style={{ left: pos.x, top: pos.y, width: NODE_W, height: NODE_H }}
      onClick={() => onSelect(person)}
      data-testid={`person-node-${person.id}`}
    >
      <div className="flex flex-col items-center gap-2 p-3 h-full">
        <div className={`member-photo-frame w-20 h-20 shrink-0 border-2 ${deceased ? "member-photo-frame-silver" : ""} ${avatarTone}`}>
          <div className="member-photo-surface">
          {photo ? (
            <img src={fileUrl(photo.path)} alt={name} style={cardPhotoStyle(photo)} className={`w-full h-full object-cover ${deceased ? "grayscale" : ""}`} />
          ) : (
            <User size={40} aria-hidden="true" />
          )}
          </div>
        </div>
        <div className="min-w-0 w-full flex-1 flex flex-col justify-end text-center">
          <div className="flex items-center justify-center gap-1">
            <p title={cardName} className="text-sm font-semibold text-stone-900 line-clamp-2 break-words leading-tight">{cardName}</p>
          </div>
        </div>
      </div>
      {ancestorMeta?.primary && ancestorMeta?.hasAncestors && (
        <button
          onClick={(e) => { e.stopPropagation(); onToggleAncestors(ancestorMeta.familyKey, person.id); }}
          className="absolute -top-3.5 left-1/2 -translate-x-1/2 w-7 h-7 rounded-full bg-[#2C1E16] text-amber-50 flex items-center justify-center shadow-md hover:bg-[#D4A359] hover:text-[#2C1E16] transition-colors z-10"
          data-testid={`ancestor-collapse-toggle-${person.id}`}
          aria-label={ancestorMeta.collapsed ? t("Expand one ancestor generation") : t("Collapse ancestors")}
          title={ancestorMeta.collapsed ? t("Show parents only") : t("Hide ancestors")}
        >
          {ancestorMeta.collapsed ? <Plus size={15} /> : <Minus size={15} />}
        </button>
      )}
      {meta?.primary && meta?.hasDescendants && (
        <button
          onClick={(e) => { e.stopPropagation(); onToggleCollapse(meta.familyKey, person.id); }}
          className="absolute -bottom-3.5 left-1/2 -translate-x-1/2 w-7 h-7 rounded-full bg-[#2C1E16] text-amber-50 flex items-center justify-center shadow-md hover:bg-[#D4A359] hover:text-[#2C1E16] transition-colors z-10"
          data-testid={`collapse-toggle-${person.id}`}
          aria-label={meta.collapsed ? t("Expand descendants") : t("Collapse descendants")}
          title={meta.collapsed ? t("Show descendants") : t("Hide descendants")}
        >
          {meta.collapsed ? <Plus size={15} /> : <Minus size={15} />}
        </button>
      )}
    </div>
  );
}

const FamilyTreeCanvas = forwardRef(function FamilyTreeCanvas(
  { persons, defaultRootId, selectedId, onSelect, maxGen, fitRevision = 0, onCollapseAll, onExpandAll, collapsedKeys, onToggleCollapse, collapsedAncestorKeys, onToggleAncestors, focusRequest, onFocusHandled },
  ref
) {
  useLanguage();
  const transformRef = useRef(null);
  const viewportRef = useRef(null);
  const [viewport, setViewport] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const element = viewportRef.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) => {
      setViewport({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const ancestorMeta = useMemo(() => {
    const { units, byId } = buildUnits(persons);
    const result = {};
    units.forEach((u) => u.members.forEach((p, index) => {
      result[p.id] = { familyKey: u.key, primary: index === 0,
        hasAncestors: u.members.some((m) => byId[m.father_id] || byId[m.mother_id]),
        collapsed: collapsedAncestorKeys?.has(u.key) };
    }));
    return result;
  }, [persons, collapsedAncestorKeys]);
  const visible = useMemo(() => {
    const expanded = collapseAncestors(persons, collapsedAncestorKeys);
    if (!maxGen) return expanded;
    const { depthById } = computeDepths(expanded);
    return expanded.filter((p) => (depthById[p.id] || 0) < maxGen);
  }, [persons, maxGen, collapsedAncestorKeys]);

  const { positions, width, height, nodeMeta } = useMemo(
    () => computeLayout(visible, collapsedKeys),
    [visible, collapsedKeys]
  );
  const points = Object.values(positions);
  const cards = useMemo(() => Object.values(positions).map((p) => ({
    left: p.x + 20, right: p.x + 20 + NODE_W,
    top: p.y + 20, bottom: p.y + 20 + NODE_H,
  })), [positions]);
  const constrainPan = (controls) => {
    const next = constrainTreePan(controls.state, viewport, cards);
    if (next !== controls.state) {
      controls.setTransform(next.positionX, next.positionY, next.scale, 0);
    }
  };
  const bounds = {
    left: (points.length ? Math.min(...points.map((p) => p.x)) : 0) + 20,
    right: Math.max(...points.map((p) => p.x + NODE_W), NODE_W) + 20,
    top: (points.length ? Math.min(...points.map((p) => p.y)) : 0) + 4,
    bottom: Math.max(...points.map((p) => p.y + NODE_H + 16), NODE_H) + 20,
  };
  const defaultRoot = positions[defaultRootId];
  const first = defaultRoot || [...points].sort((a, b) => a.y - b.y || a.x - b.x)[0];
  const start = { x: (first?.x || 0) + NODE_W / 2 + 20,
    y: defaultRoot ? defaultRoot.y + 4 : bounds.top };
  const initialView = readableTreeView(viewport, start);
  const minimumScale = fitTree(viewport, bounds, 1.1).scale;

  useImperativeHandle(ref, () => ({
    panTo: (id) => {
      if (positions[id] && transformRef.current) {
        try {
          transformRef.current.zoomToElement(`ptnode-${id}`, 1.1, 500);
        } catch (e) {
          /* element may be off-DOM briefly */
        }
      }
    },
  }), [positions]);

  const connectors = useMemo(() => {
    const byId = {};
    visible.forEach((p) => (byId[p.id] = p));
    const lines = [];
    visible.forEach((p) => {
      const cp = positions[p.id];
      if (!cp) return;
      // parent -> child: one line from the couple's midpoint (or the single parent)
      const parents = [p.father_id, p.mother_id].filter((pid) => pid && positions[pid]);
      if (parents.length) {
        const ps = parents.map((pid) => positions[pid]);
        const x1 = (Math.min(...ps.map((q) => q.x + NODE_W / 2)) + Math.max(...ps.map((q) => q.x + NODE_W / 2))) / 2;
        const y1 = Math.max(...ps.map((q) => q.y)) + NODE_H;
        const x2 = cp.x + NODE_W / 2;
        const y2 = cp.y;
        const midY = (y1 + y2) / 2;
        lines.push({ key: `pc-${p.id}`, d: `M ${x1} ${y1} V ${midY} H ${x2} V ${y2}`, type: "parent" });
      }
      // spouse
      spouseIds(p).forEach((id) => {
        if (positions[id] && (p.id < id || !spouseIds(byId[id]).includes(p.id))) {
          const sp = positions[id];
          const y = cp.y + NODE_H / 2;
          lines.push({ key: `sp-${p.id}-${id}`, d: `M ${cp.x + NODE_W} ${y} H ${sp.x}`, type: "spouse" });
        }
      });
    });
    return lines;
  }, [visible, positions]);

  return (
    <div ref={viewportRef} className="relative w-full h-full">
    {visible.length > 0 && viewport.width > 0 && viewport.height > 0 && <TransformWrapper
      ref={transformRef}
      initialScale={initialView.scale}
      initialPositionX={initialView.x}
      initialPositionY={initialView.y}
      minScale={minimumScale}
      maxScale={3}
      zoomAnimation={{ disabled: true }}
      limitToBounds={false}
      panning={{ velocityDisabled: true }}
      onPanning={constrainPan}
      wheel={{ step: 0.08 }}
      doubleClick={{ disabled: true }}
    >
      <TransformComponent wrapperStyle={{ width: "100%", height: "100%", touchAction: "none" }} contentStyle={{ width: width + 40, height: height + 40 }}>
        <div className="relative" style={{ width: width + 40, height: height + 40, padding: 20 }}>
          <svg className="absolute inset-0 pointer-events-none" width={width + 40} height={height + 40} style={{ overflow: "visible" }}>
            <g transform="translate(20,20)">
              {connectors.map((l) => (
                <path
                  key={l.key}
                  d={l.d}
                  fill="none"
                  stroke={l.type === "spouse" ? "#92400E" : "#44403C"}
                  strokeWidth={2.5}
                  vectorEffect="non-scaling-stroke"
                  strokeDasharray={l.type === "spouse" ? "5 4" : "0"}
                />
              ))}
            </g>
          </svg>
          <div className="absolute" style={{ left: 20, top: 20 }}>
            {visible.map((p) =>
              positions[p.id] ? (
                <Node
                  key={p.id}
                  person={p}
                  pos={positions[p.id]}
                  selected={p.id === selectedId}
                  onSelect={onSelect}
                  meta={nodeMeta[p.id]}
                  onToggleCollapse={onToggleCollapse}
                  ancestorMeta={ancestorMeta[p.id]}
                  onToggleAncestors={onToggleAncestors}
                />
              ) : null
            )}
          </div>
        </div>
      </TransformComponent>
      <Controls onCollapseAll={onCollapseAll} onExpandAll={onExpandAll} fitRevision={fitRevision} positions={positions} viewport={viewport} bounds={bounds} start={start} focusRequest={focusRequest} onFocusHandled={onFocusHandled} />
    </TransformWrapper>}
    </div>
  );
});

export default FamilyTreeCanvas;
